// API service for DefendSphere backend integration
import { API_ENDPOINTS } from "../config/api";
import { USE_MOCK_API } from "../mocks/useMockApi";
import mockIncidents from "../mocks/fixtures/incidents.json";
import mockSecurityHealth from "../mocks/fixtures/security-health.json";
import mockAssetsMonitoring from "../mocks/fixtures/assets-monitoring.json";
import mockCompliance from "../mocks/fixtures/compliance.json";
import mockDashboardSuppliers from "../mocks/fixtures/dashboard-suppliers.json";
import mockDashboardCustomers from "../mocks/fixtures/customers.json";
import mockFramework from "../mocks/fixtures/framework.json";
import mockTestOwners from "../mocks/fixtures/test-owners.json";

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

function extractAuthToken(): string | null {
  try {
    const raw =
      localStorage.getItem('authToken') ||
      sessionStorage.getItem('authToken') ||
      localStorage.getItem('auth_token') ||
      localStorage.getItem('token');
    const token = raw ? String(raw).trim() : '';
    return token.length > 0 ? token : null;
  } catch {
    return null;
  }
}

// Ensure authenticated requests include the stored JWT
function getAuthHeaders(): HeadersInit {
  const token = extractAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface ApiResponse<T> {
  data: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

interface Asset {
  id: string;
  name: string;
  assetType: 'SERVERS' | 'CLOUD' | 'NETWORK' | 'APPLICATION' | 'DATABASE' | 'IOT' | 'OTHER';
  environment?: 'PRODUCTION' | 'STAGING' | 'DEVELOPMENT' | 'TEST';
  compliancePct?: number;
  riskLevel: 'HIGH' | 'MEDIUM' | 'LOW' | 'NOT_ASSESSED';
  ipOrUrl?: string;
  description?: string;
  location?: string;
  lastScanAt?: string;
  createdAt: string;
  updatedAt: string;
  owner?: {
    id: string;
    username: string;
    fullName: string;
    email: string;
  };
  organization?: {
    id: string;
    name: string;
  };
  standards: Array<{
    standard: {
      id: string;
      code: string;
      name: string;
    };
  }>;
  scans: Array<{
    id: string;
    status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
    healthScore?: number;
    criticalIssues?: number;
    highIssues?: number;
    mediumIssues?: number;
    lowIssues?: number;
    createdAt: string;
    completedAt?: string;
  }>;
}

interface AssetFilters {
  page?: number;
  limit?: number;
  sort?: string;
  search?: string;
  assetType?: string;
  environment?: string;
  riskLevel?: string;
  ownerId?: string;
  organizationId?: string;
  standard?: string;
}

interface CreateAssetData {
  name: string;
  assetType: string;
  environment?: string;
  compliancePct?: number;
  riskLevel?: string;
  ipOrUrl?: string;
  description?: string;
  location?: string;
  ownerId?: string;
  organizationId?: string;
  standards?: string[];
}

interface UpdateAssetData extends Partial<CreateAssetData> {}

interface User {
  id: string;
  username: string;
  email: string;
  fullName: string;
  role: 'ADMIN' | 'USER';
  organizationId?: string;
  organization?: {
    id: string;
    name: string;
  };
  permissions: string[];
  lastLogin?: string;
  createdAt: string;
}

interface Organization {
  id: string;
  name: string;
  description?: string;
  industry?: string;
  size?: string;
  location?: string;
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  licenseType?: string;
  licenseExpiry?: string;
  maxUsers: number;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  settings?: any;
  billingInfo?: any;
  createdAt: string;
  updatedAt: string;
}

interface Standard {
  id: string;
  code: string;
  name: string;
  description?: string;
  category?: string;
  isSystem: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AssetScan {
  id: string;
  assetId: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  healthScore?: number;
  criticalIssues?: number;
  highIssues?: number;
  mediumIssues?: number;
  lowIssues?: number;
  reportLink?: string;
  createdAt: string;
  completedAt?: string;
}

interface Report {
  id: string;
  name: string;
  type: string;
  owner: string;
  department: string;
  createdDate: string;
  status: string;
  riskLevel: 'high' | 'medium' | 'low' | 'not-assessed';
  systemsChecked: number;
  incidentsFound: number;
  complianceScore: number;
  notes?: string;
  detailsCount?: number;
  filesCount?: number;
  summary?: {
    id: string;
    systemsChecked: number;
    incidentsFound: number;
    compliancePercent: number;
    highRisks: number;
    mediumRisks: number;
    lowRisks: number;
    notAssessed: number;
  };
  details?: Array<{
    id: string;
    description: string;
    findingType: string;
    severity: string;
    status: string;
    recommendations?: string;
    asset?: {
      id: string;
      name: string;
      assetType: string;
    };
  }>;
  files?: Array<{
    id: string;
    fileType: string;
    fileUrl: string;
    createdAt: string;
  }>;
}

interface ReportFilters {
  page?: number;
  limit?: number;
  reportType?: string;
  status?: string;
  riskLevel?: string;
  department?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  startDate?: string;
  endDate?: string;
}

interface CreateReportData {
  name: string;
  type: string;
  owner: string;
  department: string;
  createdDate: string;
  status: string;
  riskLevel: string;
  systemsChecked: number;
  incidentsFound: number;
  complianceScore: number;
  notes?: string;
}

interface UpdateReportData extends Partial<CreateReportData> {}

// Compliance interfaces
interface ComplianceRecord {
  id: string;
  standard: string;
  department: string;
  status: 'Compliant' | 'Partial' | 'Non-Compliant' | 'Not Assessed';
  compliancePercentage: number;
  lastAssessmentDate: string;
  nextScheduledAssessment: string;
  recommendations: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  owner?: {
    id: string;
    username: string;
    fullName: string;
    email: string;
  };
  organization?: {
    id: string;
    name: string;
  };
  history?: ComplianceHistory[];
  assessments?: ComplianceAssessment[];
}

interface ComplianceHistory {
  id: string;
  statusBefore: string | null;
  statusAfter: string;
  complianceBefore: number | null;
  complianceAfter: number;
  changedBy: {
    id: string;
    username: string;
    fullName: string;
  };
  changeReason: string | null;
  createdAt: string;
}

interface ComplianceAssessment {
  id: string;
  assessor: {
    id: string;
    username: string;
    fullName: string;
  };
  assessmentDate: string;
  complianceScore: number;
  status: string;
  findings: string | null;
  recommendations: string | null;
  nextAssessment: string | null;
  createdAt: string;
}

interface ComplianceFilters {
  standard?: string;
  status?: string;
  department?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

interface CreateComplianceData {
  standard: string;
  department: string;
  status?: string;
  compliancePercentage?: number;
  lastAssessmentDate?: string;
  nextScheduledAssessment?: string;
  recommendations?: string;
  notes?: string;
}

interface UpdateComplianceData extends Partial<CreateComplianceData> {}

interface AssessmentData {
  complianceScore?: number;
  status?: string;
  findings?: string;
  recommendations?: string;
  nextAssessment?: string;
}

interface ComplianceSummaryCards {
  totalHealth: number;
  activeStandards: number;
  lastUpdated: string | null;
  auditReadiness: string;
}

interface ComplianceSummaryDetail {
  id: string;
  standard: string;
  name: string;
  compliancePercent: number;
  status: string;
  lastAssessmentDate: string | null;
}

interface ComplianceTrendPoint {
  month: string;
  avgCompliance: number;
}

interface ComplianceStandardOverview {
  standardCode: string;
  standardName?: string;
  avgCompliance: number;
}

interface ComplianceOverview {
  organizationId: string;
  standardCode?: string | null;
  avgCompliance: number;
  trend: ComplianceTrendPoint[];
  standards: ComplianceStandardOverview[];
}

interface ComplianceWeakArea {
  code?: string;
  name?: string;
  category?: string;
  percent: number;
}

interface ComplianceSummary {
  cards: ComplianceSummaryCards;
  weakAreas: ComplianceWeakArea[];
  details: ComplianceSummaryDetail[];
  overview?: ComplianceOverview | null;
}

interface ComplianceCategorySummary {
  code: string;
  name?: string;
  percent: number;
}

interface ComplianceCategoryQuestion {
  id: string;
  code?: string;
  text?: string | null;
  text_en?: string | null;
  text_es?: string | null;
  text_ru?: string | null;
  type?: string | null;
  scaleMin?: number | null;
  scaleMax?: number | null;
  weight?: number | null;
  metadata?: Record<string, unknown> | null;
}

interface ComplianceCategoryItem {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  standardId?: string | null;
  taxonomy?: unknown;
  avgCompliance?: number;
  avgWeight?: number;
  status?: string;
  questionCount?: number;
  questions?: ComplianceCategoryQuestion[];
}

interface ComplianceHistoryEntry {
  id: string;
  date: string;
  standard: string;
  statusBefore?: string | null;
  statusAfter?: string | null;
  complianceBefore?: number | null;
  complianceAfter?: number | null;
  changedBy?: string | null;
}

interface ComplianceSurveyQuestion {
  id: string;
  code?: string;
  text?: string | null;
  text_en?: string | null;
  text_es?: string | null;
  text_ru?: string | null;
  type?: string | null;
  scaleMin?: number | null;
  scaleMax?: number | null;
  weight?: number | null;
  categoryId?: string | null;
}

interface ComplianceSurveyPayload {
  organization_id?: string;
  standard: string;
  questionnaireType?: string;
  answers?: Record<string, number>;
  compliance_score: number;
  submitted_at?: string;
  scores?: Record<string, unknown> | null;
}

interface ComplianceSurveyResponse {
  organization_id?: string;
  standard?: string;
  compliance_score?: number;
  submitted_at?: string | null;
  updated_by?: string | null;
  answers?: Record<string, number>;
  scores?: Record<string, unknown> | null;
  questions?: ComplianceSurveyQuestion[];
}

interface ComplianceStandardOption {
  code: string;
  name: string;
  description?: string | null;
  active?: boolean;
}

class ApiService {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (!headers.Authorization) {
      const runtimeToken = this.token ?? extractAuthToken();
      if (runtimeToken) {
        headers.Authorization = runtimeToken.startsWith('Bearer ')
          ? runtimeToken
          : `Bearer ${runtimeToken}`;
      }
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken();
        window.location.href = '/login';
        throw new Error('Unauthorized');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Auth endpoints
  async login(username: string, password: string) {
    const response = await this.request<{ token: string; user: User }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    
    if (response.data.token) {
      this.setToken(response.data.token);
    }
    
    return response.data;
  }

  async logout() {
    await this.request('/auth/logout', { method: 'POST' });
    this.clearToken();
  }

  async getCurrentUser() {
    return this.request<User>('/auth/me');
  }

  // changePassword defined below in User Settings section

  // Assets endpoints
  async getAssets(filters: AssetFilters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    const endpoint = queryString ? `/assets?${queryString}` : '/assets';
    
    return this.request<Asset[]>(endpoint);
  }

  async getAsset(id: string) {
    return this.request<Asset>(`/assets/${id}`);
  }

  async createAsset(data: CreateAssetData) {
    return this.request<Asset>('/assets', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAsset(id: string, data: UpdateAssetData) {
    return this.request<Asset>(`/assets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAsset(id: string) {
    return this.request(`/assets/${id}`, {
      method: 'DELETE',
    });
  }

  async scanAsset(id: string) {
    return this.request<{ scanId: string; status: string }>(`/assets/${id}/scan`, {
      method: 'POST',
    });
  }

  async getAssetScans(id: string) {
    return this.request<AssetScan[]>(`/assets/${id}/scans`);
  }

  async exportAssets(format: 'excel' | 'pdf', filters: AssetFilters = {}) {
    const params = new URLSearchParams();
    params.append('format', format);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    const endpoint = `/assets/export?${queryString}`;
    
    return this.request<{ jobId: string; format: string; status: string }>(endpoint);
  }

  // Users endpoints (admin only)
  async getUsers(filters: any = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    const endpoint = queryString ? `/users?${queryString}` : '/users';
    
    return this.request<User[]>(endpoint);
  }

  async getUser(id: string) {
    return this.request<User>(`/users/${id}`);
  }

  async createUser(data: any) {
    return this.request<User>('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: string, data: any) {
    return this.request<User>(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id: string) {
    return this.request(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  // Organizations endpoints (admin only)
  async getOrganizations(filters: any = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    const endpoint = queryString ? `/organizations?${queryString}` : '/organizations';
    
    return this.request<Organization[]>(endpoint);
  }

  async getOrganization(id: string) {
    return this.request<Organization>(`/organizations/${id}`);
  }

  async createOrganization(data: any) {
    return this.request<Organization>('/organizations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateOrganization(id: string, data: any) {
    return this.request<Organization>(`/organizations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteOrganization(id: string) {
    return this.request(`/organizations/${id}`, {
      method: 'DELETE',
    });
  }

  async getOrganizationStats(id: string) {
    return this.request<any>(`/organizations/${id}/stats`);
  }

  // Standards endpoints
  async getStandards(filters: any = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    const endpoint = queryString ? `/standards?${queryString}` : '/standards';
    
    return this.request<Standard[]>(endpoint);
  }

  async getStandard(id: string) {
    return this.request<Standard>(`/standards/${id}`);
  }

  async createStandard(data: any) {
    return this.request<Standard>('/standards', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateStandard(id: string, data: any) {
    return this.request<Standard>(`/standards/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteStandard(id: string) {
    return this.request(`/standards/${id}`, {
      method: 'DELETE',
    });
  }

  async getStandardCategories() {
    return this.request<string[]>('/standards/categories');
  }

  async seedStandards() {
    return this.request<any>('/standards/seed', {
      method: 'POST',
    });
  }

  // Audit endpoints (admin only)
  async getAuditLogs(filters: any = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    const endpoint = queryString ? `/audit?${queryString}` : '/audit';
    
    return this.request<any[]>(endpoint);
  }

  async getAuditLog(id: string) {
    return this.request<any>(`/audit/${id}`);
  }

  async getAuditStats(filters: any = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    const endpoint = queryString ? `/audit/stats?${queryString}` : '/audit/stats';
    
    return this.request<any>(endpoint);
  }

  async exportAuditLogs(format: 'excel' | 'pdf' | 'csv', filters: any = {}) {
    const params = new URLSearchParams();
    params.append('format', format);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    const endpoint = `/audit/export?${queryString}`;
    
    return this.request<any>(endpoint);
  }

  // Reports endpoints
  async getReports(filters: ReportFilters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    const endpoint = queryString ? `/reports?${queryString}` : '/reports';
    
    return this.request<{ reports: Report[]; pagination: any }>(endpoint);
  }

  async getReport(id: string) {
    return this.request<{ report: Report }>(`/reports/${id}`);
  }

  async createReport(data: CreateReportData) {
    return this.request<{ report: Report }>('/reports', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateReport(id: string, data: UpdateReportData) {
    return this.request<{ report: Report }>(`/reports/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteReport(id: string) {
    return this.request(`/reports/${id}`, {
      method: 'DELETE',
    });
  }

  async exportReport(format: 'pdf' | 'excel', reportId?: string) {
    const params = new URLSearchParams();
    params.append('format', format);
    if (reportId) {
      params.append('reportId', reportId);
    }

    const queryString = params.toString();
    const endpoint = `/reports/export?${queryString}`;
    
    return this.request<{ message: string; downloadUrl: string; status: string }>(endpoint);
  }

  async getReportSummary() {
    return this.request<any>('/reports/summary');
  }

  async getReportVulnerabilities() {
    return this.request<any>('/reports/vulnerabilities');
  }

  async getReportAssets() {
    return this.request<any>('/reports/assets');
  }

  async getReportCompliance() {
    return this.request<any>('/reports/compliance');
  }

  async getFullReport() {
    return this.request<any>('/reports/full');
  }

  async generateReportFromAssets(data: {
    reportName?: string;
    reportType?: string;
    assetIds: string[];
    includeCompliance?: boolean;
    includeVulnerabilities?: boolean;
    includeScans?: boolean;
  }) {
    return this.request<{ report: Report; message: string }>('/reports/generate-from-assets', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getAssetsForReportGeneration() {
    return this.request<{ assets: any[] }>('/reports/assets-for-generation');
  }

  // Analytics methods
  async getReportTrends(period: string = '30d') {
    return this.request<{ trends: any[] }>(`/reports/analytics/trends?period=${period}`);
  }

  async getReportComparison(period1: string = '30d', period2: string = '60d') {
    return this.request<{ comparison: any }>(`/reports/analytics/comparison?period1=${period1}&period2=${period2}`);
  }

  async getReportPredictions() {
    return this.request<{ predictions: any[], currentTrends: any }>('/reports/analytics/predictions');
  }

  // Compliance methods
  async getComplianceRecords(filters: ComplianceFilters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const queryString = params.toString();
    return this.request<{ data: ComplianceRecord[], pagination: any }>(`/compliance${queryString ? `?${queryString}` : ''}`);
  }

  async getComplianceRecord(id: string) {
    return this.request<{ data: ComplianceRecord }>(`/compliance/${id}`);
  }

  async createComplianceRecord(data: CreateComplianceData) {
    return this.request<{ data: ComplianceRecord }>('/compliance', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateComplianceRecord(id: string, data: UpdateComplianceData) {
    return this.request<{ data: ComplianceRecord }>(`/compliance/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteComplianceRecord(id: string) {
    return this.request<{ message: string }>(`/compliance/${id}`, {
      method: 'DELETE',
    });
  }

  async getComplianceSummary(options: { categoryCode?: string; standardCode?: string } = {}) {
    const headers = new Headers(getAuthHeaders());
    if (this.token) headers.set('Authorization', `Bearer ${this.token}`);

    const params = new URLSearchParams();
    if (options.categoryCode) params.set('category', options.categoryCode);
    if (options.standardCode) params.set('standard', options.standardCode);

    const query = params.toString();
    const response = await fetch(
      `${API_ENDPOINTS.COMPLIANCE_SUMMARY}${query ? `?${query}` : ''}`,
      { headers },
    );
    if (!response.ok) {
      throw new Error(`Failed to load compliance summary (${response.status})`);
    }
    return response.json() as Promise<{ success: boolean; data: ComplianceSummary }>;
  }

    async getComplianceCategoriesSummary(categoryCode?: string) {
    const headers = new Headers(getAuthHeaders());
    if (this.token) headers.set('Authorization', `Bearer ${this.token}`);

      const params = new URLSearchParams();
      if (categoryCode) params.set('category', categoryCode);

      const response = await fetch(`${API_ENDPOINTS.COMPLIANCE_CATEGORIES_SUMMARY}${params.toString() ? `?${params.toString()}` : ''}`, { headers });
    if (!response.ok) {
      throw new Error(`Failed to load compliance categories summary (${response.status})`);
    }
    return response.json() as Promise<{ success: boolean; data: ComplianceCategorySummary[] }>;
  }

  async getComplianceCategories(options: { organizationId?: string | number; standardCode?: string } = {}) {
    const headers = new Headers(getAuthHeaders());
    if (this.token) headers.set('Authorization', `Bearer ${this.token}`);

    const params = new URLSearchParams();
    if (options.organizationId != null) {
      params.set('organization_id', String(options.organizationId));
    }
    if (options.standardCode) {
      params.set('standard', options.standardCode);
    }

    const query = params.toString();
    const url = `${API_ENDPOINTS.COMPLIANCE_CATEGORIES}${query ? `?${query}` : ''}`;
    console.log('[Compliance] Fetch categories URL:', url);
    const response = await fetch(
      url,
      { headers },
    );
    if (!response.ok) {
      throw new Error(`Failed to load compliance categories (${response.status})`);
    }

    const payload = await response.json().catch(() => null);
    if (!payload || payload.success !== true) {
      const message = payload?.message || 'Failed to load compliance categories';
      throw new Error(message);
    }

    const categories = Array.isArray(payload.categories) ? payload.categories : [];
    return {
      success: true as const,
      categories: categories as ComplianceCategoryItem[],
    };
  }

  async postComplianceAnswers(payload: {
    organizationId: string;
    standardCode: string;
    categoryId: string;
    answers: Array<{ question_id: string; value: number; updatedAt: string }>;
  }) {
    const headers = new Headers({
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    });
    if (this.token) headers.set("Authorization", `Bearer ${this.token}`);

    const response = await fetch(`${API_BASE_URL}/compliance/answers`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        organization_id: payload.organizationId,
        standard: payload.standardCode,
        category_id: payload.categoryId,
        answers: payload.answers,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to save compliance answers (${response.status})`);
    }

    return response.json().catch(() => ({ success: true }));
  }

  async uploadComplianceReport(params: {
    file: File;
    organizationId?: string | number;
    standardCode?: string;
  }) {
    const headers = new Headers(getAuthHeaders());
    if (this.token) headers.set("Authorization", `Bearer ${this.token}`);

    const formData = new FormData();
    formData.append("file", params.file);
    if (params.organizationId != null) {
      formData.append("organization_id", String(params.organizationId));
    }
    if (params.standardCode) {
      formData.append("standard", params.standardCode);
    }

    const response = await fetch(`${API_BASE_URL}/compliance/import`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Failed to import compliance report (${response.status})`);
    }

    return response.json().catch(() => ({ success: true }));
  }

  async getComplianceHistory(categoryCode?: string) {
    const headers = new Headers(getAuthHeaders());
    if (this.token) headers.set('Authorization', `Bearer ${this.token}`);

      const params = new URLSearchParams();
      if (categoryCode) params.set('category', categoryCode);

      const response = await fetch(`${API_ENDPOINTS.COMPLIANCE_HISTORY}${params.toString() ? `?${params.toString()}` : ''}`, { headers });
    if (!response.ok) {
      throw new Error(`Failed to load compliance history (${response.status})`);
    }
    return response.json() as Promise<{ success: boolean; data: ComplianceHistoryEntry[] }>;
  }

  async getComplianceSurvey(standard: string, organizationId?: string | number) {
    const params = new URLSearchParams({ standard });
    if (organizationId != null) params.set('organization_id', String(organizationId));

    const headers = new Headers(getAuthHeaders());
    if (this.token) headers.set('Authorization', `Bearer ${this.token}`);

    const response = await fetch(
      `${API_ENDPOINTS.COMPLIANCE_SURVEY}?${params.toString()}`,
      { headers },
    );
    if (!response.ok) {
      throw new Error(`Failed to load compliance survey (${response.status})`);
    }
    return response.json() as Promise<ComplianceSurveyResponse>;
  }

  async getExtendedQuestionnaire(
    standardCode: string,
    questionnaireType: 'SELF_ASSESSMENT' | 'DOCUMENTS' | 'PROCESSES' | 'ROLES' | 'ANNEX_A_CONTROLS' | 'GDPR_CONTROLS' | 'DORA_CONTROLS' | 'ENS_CONTROLS' | 'SOC2_CONTROLS' | 'NIS2_CONTROLS' | 'PCI_DSS_2025_CONTROLS' | 'SWIFT_CSCF_2025_CONTROLS',
    organizationId?: string
  ) {
    const params = new URLSearchParams();
    params.set('standard', standardCode);
    params.set('questionnaireType', questionnaireType);
    params.set('type', questionnaireType);
    if (organizationId) params.set('organization_id', organizationId);

    const response = await this.request<{
      success: boolean;
      data: {
        answers: Record<string, string>;
        blockStats: Record<string, Record<string, number>>;
        overallStats: { yes: number; no: number; n_a: number; total: number };
        lastSavedAt: string | null;
      };
    }>(`/compliance/extended-questionnaire?${params.toString()}`, {
      method: 'GET',
    });

    if (!response.success && 'error' in response) {
      throw new Error(`Failed to load extended questionnaire`);
    }
    return response;
  }

  async saveExtendedQuestionnaire(data: {
    standardCode: string;
    questionnaireType: 'SELF_ASSESSMENT' | 'DOCUMENTS' | 'PROCESSES' | 'ROLES' | 'ANNEX_A_CONTROLS' | 'GDPR_CONTROLS' | 'DORA_CONTROLS' | 'ENS_CONTROLS' | 'SOC2_CONTROLS' | 'NIS2_CONTROLS' | 'PCI_DSS_2025_CONTROLS' | 'SWIFT_CSCF_2025_CONTROLS';
    answers: Record<string, string>;
    blockStats?: Record<string, Record<string, number>>;
    overallStats?: { yes: number; no: number; n_a: number; total: number };
    organizationId?: string;
  }) {
    const response = await this.request<{
      success: boolean;
      data: {
        id: string;
        answers: Record<string, string>;
        blockStats: Record<string, Record<string, number>>;
        overallStats: { yes: number; no: number; n_a: number; total: number };
        lastSavedAt: string | null;
      };
    }>('/compliance/extended-questionnaire', {
      method: 'POST',
      body: JSON.stringify({
        standardCode: data.standardCode,
        questionnaireType: data.questionnaireType,
        answers: data.answers,
        blockStats: data.blockStats || {},
        overallStats: data.overallStats || { yes: 0, no: 0, n_a: 0, total: 0 },
        ...(data.organizationId && { organization_id: data.organizationId }),
      }),
    });

    if (!response.success && 'error' in response) {
      throw new Error(`Failed to save extended questionnaire`);
    }
    return response;
  }

  async saveComplianceSurvey(data: ComplianceSurveyPayload) {
    const response = await this.request<{ success?: boolean; message?: string }>('/compliance/survey', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  async migrateShortToExtended(standardCode: string, organizationId?: string) {
    const response = await this.request<{
      success: boolean;
      message?: string;
      data?: { mappedCount: number };
    }>('/compliance/migrate-short-to-extended', {
      method: 'POST',
      body: JSON.stringify({
        standardCode,
        ...(organizationId && { organization_id: organizationId }),
      }),
    });
    return response;
  }

  async getAuditReadiness(standardCode?: string) {
    const params = new URLSearchParams();
    if (standardCode) params.set("standardCode", standardCode);
    const query = params.toString();
    const response = await fetch(
      `${API_ENDPOINTS.COMPLIANCE_AUDIT_READINESS}${query ? `?${query}` : ""}`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      throw new Error(`Failed to load audit readiness (${response.status})`);
    }
    return response.json() as Promise<{ success: boolean; data?: any }>;
  }

  async getComplianceStandards(organizationId?: string | number) {
    const params = new URLSearchParams();
    if (organizationId != null) params.set('organization_id', String(organizationId));

    const headers: Record<string, string> = { ...getAuthHeaders() };
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

    const response = await fetch(
      `${API_ENDPOINTS.COMPLIANCE_STANDARDS}${params.toString() ? `?${params.toString()}` : ''}`,
      { headers },
    );
    if (!response.ok) {
      throw new Error(`Failed to load compliance standards (${response.status})`);
    }
    return response.json() as Promise<{ success: boolean; data: ComplianceStandardOption[] }>;
  }

  async assessComplianceRecord(id: string, assessmentData: AssessmentData) {
    return this.request<{ data: { record: ComplianceRecord, assessment: any } }>(`/compliance/${id}/assess`, {
      method: 'POST',
      body: JSON.stringify({ assessmentData }),
    });
  }

  async syncComplianceWithAssets(assetIds: string[], standardId: string) {
    return this.request<{ data: any[] }>('/compliance/sync-with-assets', {
      method: 'POST',
      body: JSON.stringify({ assetIds, standardId }),
    });
  }

  async getAssetsForComplianceSync(standardId?: string) {
    const params = standardId ? `?standardId=${standardId}` : '';
    return this.request<{ data: any[] }>(`/compliance/assets-for-sync${params}`);
  }

  // Customer Trust methods
  async getCustomerTrustRecords(filters: CustomerTrustFilters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const queryString = params.toString();
    return this.request<{ data: CustomerTrust[], pagination?: any }>(`/customer-trust${queryString ? `?${queryString}` : ''}`);
  }

  async getCustomerTrustRecord(id: string) {
    return this.request<{ data: CustomerTrust }>(`/customer-trust/${id}`);
  }

  async createCustomerTrustRecord(data: CreateCustomerTrustData) {
    return this.request<{ data: CustomerTrust }>('/customer-trust', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCustomerTrustRecord(id: string, data: UpdateCustomerTrustData) {
    return this.request<{ data: CustomerTrust }>(`/customer-trust/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCustomerTrustRecord(id: string) {
    return this.request<{ data: void }>(`/customer-trust/${id}`, {
      method: 'DELETE',
    });
  }

  async getCustomerTrustSummary() {
    return this.request<{ data: CustomerTrustSummary }>('/customer-trust/summary');
  }

  async assessCustomerTrustRecord(id: string, assessmentData: AssessmentData) {
    return this.request<{ data: CustomerTrustAssessment }>(`/customer-trust/${id}/assess`, {
      method: 'POST',
      body: JSON.stringify(assessmentData),
    });
  }

  async syncCustomerTrustWithAssets(assetIds: string[], customerTrustId: string) {
    return this.request<{ data: any }>('/customer-trust/sync-with-assets', {
      method: 'POST',
      body: JSON.stringify({ assetIds, customerTrustId }),
    });
  }

  async getAssetsForCustomerTrustSync(customerTrustId?: string) {
    const params = customerTrustId ? `?customerTrustId=${customerTrustId}` : '';
    return this.request<{ data: any[] }>(`/customer-trust/assets-for-sync${params}`);
  }

  // Health check
  async healthCheck() {
    return this.request<any>('/health');
  }

  // Starter Guide methods
  async getStarterGuideQuestionnaire() {
    return this.request<{ data: StarterGuideQuestionnaire }>('/starter-guide');
  }

  async createStarterGuideQuestionnaire(data: CreateStarterGuideData) {
    return this.request<{ data: StarterGuideQuestionnaire }>('/starter-guide', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateStarterGuideQuestionnaire(id: string, data: UpdateStarterGuideData) {
    return this.request<{ data: StarterGuideQuestionnaire }>(`/starter-guide/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteStarterGuideQuestionnaire(id: string) {
    return this.request<{ data: void }>(`/starter-guide/${id}`, {
      method: 'DELETE',
    });
  }

  async exportStarterGuideQuestionnaire(id: string, format: 'pdf' | 'excel') {
    return this.request<{ data: any }>(`/starter-guide/${id}/export?format=${format}`);
  }

  async getStarterGuideStats() {
    return this.request<{ data: StarterGuideStats }>('/starter-guide/stats');
  }

  async getStarterGuideHistory(id: string) {
    return this.request<{ data: QuestionnaireHistory[] }>(`/starter-guide/${id}/history`);
  }

  async getStarterGuideRelatedEntities(id: string) {
    return this.request<{ data: any }>(`/starter-guide/${id}/related-entities`);
  }

  async syncStarterGuideCompliance(id: string) {
    return this.request<{ data: any }>(`/starter-guide/${id}/sync-compliance`, {
      method: 'POST',
    });
  }

  // User Settings methods
  async getUserSettings() {
    return this.request<{ data: UserSettings }>('/user-settings');
  }

  async updateUserProfile(data: UpdateUserProfileData) {
    return this.request<{ data: UserSettings }>('/user-settings/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async changePassword(data: ChangePasswordData) {
    return this.request<{ message: string }>('/user-settings/password', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateNotifications(data: UpdateNotificationsData) {
    return this.request<{ message: string }>('/user-settings/notifications', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateSecuritySettings(data: UpdateSecuritySettingsData) {
    return this.request<{ message: string }>('/user-settings/security', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateAppearanceSettings(data: UpdateAppearanceSettingsData) {
    return this.request<{ message: string }>('/user-settings/appearance', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getUserSettingsHistory(params?: { page?: number; limit?: number; field?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.field) queryParams.append('field', params.field);
    
    return this.request<{ data: UserSettingsHistory[]; pagination: PaginationInfo }>(`/user-settings/history?${queryParams}`);
  }

  async getUserSecurityLogs(params?: { page?: number; limit?: number; actionType?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.actionType) queryParams.append('actionType', params.actionType);
    
    return this.request<{ data: UserSecurityLog[]; pagination: PaginationInfo }>(`/user-settings/security-logs?${queryParams}`);
  }

  async exportUserSettings(format: 'json' = 'json') {
    return this.request<{ data: any }>('/user-settings/export', {
      method: 'POST',
      body: JSON.stringify({ format }),
    });
  }

  // 2FA methods
  async setup2FA(method: 'SMS' | 'AUTHENTICATOR_APP' | 'EMAIL') {
    return this.request<{ message: string; data: { method: string; secret: string | null; qrCode: string | null } }>('/user-settings/2fa/setup', {
      method: 'POST',
      body: JSON.stringify({ method }),
    });
  }

  async verify2FA(token: string, method: string) {
    return this.request<{ message: string }>('/user-settings/2fa/verify', {
      method: 'POST',
      body: JSON.stringify({ token, method }),
    });
  }

  async disable2FA(currentPassword: string) {
    return this.request<{ message: string }>('/user-settings/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ currentPassword }),
    });
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;

// User Settings interfaces
interface UserSettings {
  id: string;
  username: string;
  email: string;
  fullName: string | null;
  role: string;
  organization: {
    id: string;
    name: string;
    industry: string | null;
    size: string | null;
  } | null;
  twoFactorEnabled: boolean;
  twoFactorMethod: string | null;
  theme: string;
  language: string;
  timezone: string;
  notifications: {
    email: boolean;
    push: boolean;
    system: boolean;
    securityAlerts: boolean;
    systemUpdates: boolean;
    weeklyReports: boolean;
    monthlyReports: boolean;
  };
  simplifiedUI: boolean;
  sessionTimeout: number;
  passwordExpiry: number;
  maxLoginAttempts: number;
  ipWhitelist: string[];
  biometricAuth: boolean;
  deviceTrust: boolean;
  lastLogin: string | null;
  createdAt: string;
  updatedAt: string;
}

interface UpdateUserProfileData {
  fullName?: string;
  email?: string;
}

interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface UpdateNotificationsData {
  notifications: {
    email?: boolean;
    push?: boolean;
    system?: boolean;
    securityAlerts?: boolean;
    systemUpdates?: boolean;
    weeklyReports?: boolean;
    monthlyReports?: boolean;
  };
}

interface UpdateSecuritySettingsData {
  twoFactorEnabled?: boolean;
  twoFactorMethod?: string;
  sessionTimeout?: number;
  passwordExpiry?: number;
  maxLoginAttempts?: number;
  ipWhitelist?: string[];
  biometricAuth?: boolean;
  deviceTrust?: boolean;
}

interface UpdateAppearanceSettingsData {
  theme?: string;
  language?: string;
  timezone?: string;
  simplifiedUI?: boolean;
  dateFormat?: string;
  timeFormat?: string;
  numberFormat?: string;
  compactMode?: boolean;
  highContrast?: boolean;
  reducedMotion?: boolean;
}

interface UserSettingsHistory {
  id: string;
  userId: string;
  fieldName: string;
  oldValue: any;
  newValue: any;
  changeReason: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

interface UserSecurityLog {
  id: string;
  userId: string;
  actionType: string;
  ipAddress: string | null;
  userAgent: string | null;
  deviceInfo: any;
  success: boolean;
  details: any;
  createdAt: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

// Starter Guide interfaces
interface StarterGuideQuestionnaire {
  id: string;
  userId: string;
  organizationId: string;
  companyName: string;
  sector: string;
  hasInformationSystems: boolean;
  systemLocation?: string;
  processesEUCitizenData: boolean;
  dataTypes?: string[];
  hasSecurityStrategy: string;
  hasSecurityResponsible: boolean;
  hasCybersecurityTeam: string;
  hasDisasterRecoveryPlan: string;
  testsVulnerabilities: string;
  wantsVulnerabilityReport: boolean;
  publicResources?: string[];
  relevantStandards: string[];
  plansCertifiedAudit: string;
  interestedInPreAudit: boolean;
  wantsSelfAssessment: boolean;
  contactName: string;
  email: string;
  phone: string;
  privacyPolicyAccepted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateStarterGuideData {
  companyName: string;
  sector: string;
  hasInformationSystems: boolean;
  systemLocation?: string;
  processesEUCitizenData: boolean;
  dataTypes?: string[];
  hasSecurityStrategy: string;
  hasSecurityResponsible: boolean;
  hasCybersecurityTeam: string;
  hasDisasterRecoveryPlan: string;
  testsVulnerabilities: string;
  wantsVulnerabilityReport: boolean;
  publicResources?: string[];
  relevantStandards: string[];
  plansCertifiedAudit: string;
  interestedInPreAudit: boolean;
  wantsSelfAssessment: boolean;
  contactName: string;
  email: string;
  phone: string;
  privacyPolicyAccepted: boolean;
}

interface UpdateStarterGuideData extends Partial<CreateStarterGuideData> {}

interface QuestionnaireHistory {
  id: string;
  questionnaireId: string;
  changedBy: string;
  changeType: string;
  oldValues?: any;
  newValues?: any;
  changeReason?: string;
  createdAt: string;
  user: {
    id: string;
    fullName: string;
    email: string;
  };
}

interface StarterGuideStats {
  total: number;
  bySector: Record<string, number>;
  byCompliance: {
    high: number;
    medium: number;
    low: number;
  };
  recentSubmissions: number;
}

// Customer Trust interfaces
interface CustomerTrust {
  id: string;
  name: string;
  category: 'CLIENT' | 'PARTNER';
  sector: string;
  assignedStandards: string[];
  compliancePercentage: number;
  lastAssessment: string;
  responsiblePerson: string;
  email: string;
  website?: string;
  createdAt: string;
  updatedAt: string;
  owner?: {
    id: string;
    fullName: string;
    email: string;
  };
  organization?: {
    id: string;
    name: string;
  };
  assessments?: CustomerTrustAssessment[];
  history?: CustomerTrustHistory[];
}

interface CustomerTrustAssessment {
  id: string;
  assessmentDate: string;
  complianceScore: number;
  status: 'COMPLIANT' | 'PARTIAL' | 'NON_COMPLIANT' | 'NOT_ASSESSED';
  findings?: string;
  recommendations?: string;
  nextAssessment?: string;
  assessor: {
    id: string;
    fullName: string;
    email: string;
  };
  createdAt: string;
}

interface CustomerTrustHistory {
  id: string;
  complianceBefore?: number;
  complianceAfter: number;
  changeReason?: string;
  changedBy: {
    id: string;
    fullName: string;
    email: string;
  };
  createdAt: string;
}

interface CustomerTrustFilters {
  page?: number;
  limit?: number;
  category?: string;
  sector?: string;
  standard?: string;
  complianceLevel?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface CreateCustomerTrustData {
  name: string;
  category: 'CLIENT' | 'PARTNER';
  sector: string;
  assignedStandards: string[];
  compliancePercentage: number;
  lastAssessment?: string;
  responsiblePerson: string;
  email: string;
  website?: string;
}

interface UpdateCustomerTrustData {
  name?: string;
  category?: 'CLIENT' | 'PARTNER';
  sector?: string;
  assignedStandards?: string[];
  compliancePercentage?: number;
  lastAssessment?: string;
  responsiblePerson?: string;
  email?: string;
  website?: string;
}

interface CustomerTrustSummary {
  totalRecords: number;
  avgCompliance: number;
  categoryDistribution: Record<string, number>;
  sectorDistribution: Record<string, number>;
  complianceDistribution: Record<string, number>;
}

// Admin Panel Interfaces
interface AdminUser {
  id: string;
  username: string;
  email: string;
  fullName?: string;
  role: 'ADMIN' | 'USER' | 'AUDITOR' | 'MANAGER';
  isActive: boolean;
  emailVerified: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
  organization?: {
    id: string;
    name: string;
  };
  userLicenses: Array<{
    id: string;
    licenseType: 'Full' | 'Trial' | 'Module-based';
    assignedAt: string;
    expiresAt?: string;
  }>;
  userModules: Array<{
    id: string;
    moduleName: string;
    enabled: boolean;
    assignedAt: string;
  }>;
  accessRights: Array<{
    id: string;
    sectionName: string;
    permission: 'read' | 'write' | 'admin';
    grantedAt: string;
  }>;
  securityLogs?: Array<{
    id: string;
    actionType: string;
    ipAddress?: string;
    userAgent?: string;
    success: boolean;
    createdAt: string;
  }>;
  activityLogs?: Array<{
    id: string;
    actionType: string;
    targetType: string;
    targetId?: string;
    actionDetails?: any;
    createdAt: string;
  }>;
}

interface AdminUserFilters {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: 'active' | 'inactive';
  organizationId?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface CreateAdminUserData {
  username: string;
  email: string;
  fullName: string;
  password: string;
  role: 'ADMIN' | 'USER' | 'AUDITOR' | 'MANAGER';
  organizationId: string;
  isActive?: boolean;
  emailVerified?: boolean;
  licenses?: Array<{
    licenseType: 'Full' | 'Trial' | 'Module-based';
    expiresAt?: string;
  }>;
  modules?: string[];
  accessRights?: Array<{
    sectionName: string;
    permission: 'read' | 'write' | 'admin';
  }>;
}

interface UpdateAdminUserData {
  username?: string;
  email?: string;
  fullName?: string;
  role?: 'ADMIN' | 'USER' | 'AUDITOR' | 'MANAGER';
  organizationId?: string;
  isActive?: boolean;
  emailVerified?: boolean;
  licenses?: Array<{
    licenseType: 'Full' | 'Trial' | 'Module-based';
    expiresAt?: string;
  }>;
  modules?: string[];
  accessRights?: Array<{
    sectionName: string;
    permission: 'read' | 'write' | 'admin';
  }>;
}

interface ActivityLog {
  id: string;
  userId: string;
  actionType: string;
  targetType: string;
  targetId?: string;
  actionDetails?: any;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
  user: {
    id: string;
    username: string;
    fullName?: string;
    email: string;
    organization?: {
      id: string;
      name: string;
    };
  };
}

interface ActivityLogFilters {
  page?: number;
  limit?: number;
  userId?: string;
  actionType?: string;
  targetType?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface AdminDashboardStats {
  totalUsers: number;
  activeUsers: number;
  totalOrganizations: number;
  recentActivity: number;
  userRoleStats: Array<{
    role: string;
    count: number;
  }>;
  licenseStats: Array<{
    licenseType: string;
    count: number;
  }>;
  moduleStats: Array<{
    moduleName: string;
    count: number;
  }>;
}

// Export types
export type {
  Asset,
  AssetFilters,
  CreateAssetData,
  UpdateAssetData,
  User,
  Organization,
  Standard,
  AssetScan,
  Report,
  ReportFilters,
  CreateReportData,
  UpdateReportData,
  ComplianceRecord,
  ComplianceHistory,
  ComplianceAssessment,
  ComplianceFilters,
  CreateComplianceData,
  UpdateComplianceData,
  AssessmentData,
  ComplianceSummary,
  CustomerTrust,
  CustomerTrustAssessment,
  CustomerTrustHistory,
  CustomerTrustFilters,
  CreateCustomerTrustData,
  UpdateCustomerTrustData,
  CustomerTrustSummary,
  StarterGuideQuestionnaire,
  AdminUser,
  AdminUserFilters,
  CreateAdminUserData,
  UpdateAdminUserData,
  ActivityLog,
  ActivityLogFilters,
  AdminDashboardStats,
  CreateStarterGuideData,
  UpdateStarterGuideData,
  QuestionnaireHistory,
  StarterGuideStats,
  UserSettings,
  UpdateUserProfileData,
  ChangePasswordData,
  UpdateNotificationsData,
  UpdateSecuritySettingsData,
  UpdateAppearanceSettingsData,
  UserSettingsHistory,
  UserSecurityLog,
  PaginationInfo,
  ApiResponse,
};

// Admin Panel API methods
export const adminApi = {
  // Users Management
  async getUsers(filters: AdminUserFilters = {}): Promise<ApiResponse<AdminUser[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });

    const response = await fetch(`${API_BASE_URL}/admin/users?${params}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }

    const data = await response.json();
    return {
      data: data.users,
      pagination: data.pagination
    };
  },

  async getUser(id: string): Promise<AdminUser> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user');
    }

    return response.json();
  },

  async createUser(userData: CreateAdminUserData): Promise<AdminUser> {
    const response = await fetch(`${API_BASE_URL}/admin/users`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create user');
    }

    const data = await response.json();
    return data.user;
  },

  async updateUser(id: string, userData: UpdateAdminUserData): Promise<AdminUser> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update user');
    }

    const data = await response.json();
    return data.user;
  },

  async deleteUser(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete user');
    }
  },

  async resetUserPassword(id: string, newPassword: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${id}/reset-password`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ newPassword }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to reset password');
    }
  },

  async toggleUserStatus(id: string): Promise<AdminUser> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${id}/toggle-status`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to toggle user status');
    }

    const data = await response.json();
    return data.user;
  },

  // Activity Logs
  async getActivityLogs(filters: ActivityLogFilters = {}): Promise<ApiResponse<ActivityLog[]>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });

    const response = await fetch(`${API_BASE_URL}/admin/activity-logs?${params}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch activity logs');
    }

    const data = await response.json();
    return {
      data: data.logs,
      pagination: data.pagination
    };
  },

  // Dashboard Stats
  async getDashboardStats(): Promise<AdminDashboardStats> {
    // Check if user has admin role before making the request
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token');
    }
    
    // Decode token to check role (simple base64 decode)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.role !== 'admin') {
        throw new Error('Access denied: Admin role required');
      }
    } catch (e) {
      throw new Error('Invalid token or access denied');
    }

    const response = await fetch(`${API_BASE_URL}/admin/dashboard-stats`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch dashboard stats');
    }

    return response.json();
  },

  // Organizations for Admin Panel
  async getOrganizations(): Promise<Organization[]> {
    const response = await fetch(`${API_BASE_URL}/admin/organizations`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch organizations');
    }

    return response.json();
  },
};

// Home Dashboard API
export const homeApi = {
  getUnifiedAssessmentSnapshots: async (standards?: string[]): Promise<{
    source: string;
    organizationId: string;
    overallCompliance: number;
    standards: Array<{
      standardCode: string;
      selfAssessmentScore: number;
      verifiedScore: number;
      weakAreas: Array<{ code?: string; name?: string; percent: number }>;
      recommendationsInput: Record<string, unknown>;
    }>;
    weakAreas: Array<{ standardCode?: string; code?: string; name?: string; percent: number }>;
  }> => {
    const params = new URLSearchParams();
    if (Array.isArray(standards) && standards.length > 0) {
      params.set("standards", standards.join(","));
    }
    const response = await fetch(
      `${API_BASE_URL}/compliance/unified-assessment-snapshots${params.toString() ? `?${params.toString()}` : ""}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) {
      throw new Error("Failed to fetch unified assessment snapshots");
    }
    const payload = await response.json();
    return payload?.data || {
      source: "none",
      organizationId: "",
      overallCompliance: 0,
      standards: [],
      weakAreas: [],
    };
  },

  // Security Health
  getSecurityHealth: async (): Promise<{
    percent: number | null
    status: string
    lastScan: string | null
    severity?: { critical: number; high: number; medium: number; low: number; info?: number }
    currentUniqueFindings?: number
    healthKind?: string
    healthFormula?: string
    showPercent?: boolean
    assessmentStatus?: string
    assessmentWarning?: string | null
  }> => {
    if (USE_MOCK_API) {
      return Promise.resolve(mockSecurityHealth as { percent: number; status: string; lastScan: string | null });
    }
    const { getActiveOrgId } = await import('../utils/orgUtils')
    const orgId = getActiveOrgId()
    const orgQuery = orgId ? `?organization_id=${encodeURIComponent(orgId)}` : ''
    const response = await fetch(`${API_BASE_URL}/home/security-health${orgQuery}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch security health');
    }
    
    return response.json();
  },

  // Assets Monitoring — optional *_delta (or nested deltas) for Home ProblemCard footers / tooltips
  getAssetsMonitoring: async (): Promise<{
    critical: number
    high: number
    medium: number
    low: number
    total: number
    delta?: number
    critical_delta?: number
    high_delta?: number
    medium_delta?: number
    low_delta?: number
    deltas?: Partial<Record<'critical' | 'high' | 'medium' | 'low', number>>
    informationSystemsCount?: number
    assetsCount?: number
    problems?: {
      total: number
      vulnerabilities: number
      risks: number
      controls: number
    }
  }> => {
    if (USE_MOCK_API) {
      return Promise.resolve(mockAssetsMonitoring);
    }
    const { getActiveOrgId } = await import('../utils/orgUtils')
    const orgId = getActiveOrgId()
    const orgQuery = orgId ? `?organization_id=${encodeURIComponent(orgId)}` : ''
    const response = await fetch(`${API_BASE_URL}/home/assets-monitoring${orgQuery}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch assets monitoring data');
    }
    
    return response.json();
  },

  // Scan History
  getScanHistory: async (days: number = 30): Promise<Array<{date: string, health: number}>> => {
    const response = await fetch(`${API_BASE_URL}/home/scan-history?days=${days}`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch scan history');
    }
    
    return response.json();
  },

  // Criticality Levels
  getCriticalityLevels: async (): Promise<{assets: string, compliance: string, suppliers: string}> => {
    const response = await fetch(`${API_BASE_URL}/home/criticality-levels`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch criticality levels');
    }
    
    return response.json();
  }
};

// Dashboard (Home) API - org-scoped
export const dashboardApi = {
  async getIncidents() {
    if (USE_MOCK_API) {
      return Promise.resolve(mockIncidents);
    }
    const response = await fetch(`${API_BASE_URL}/dashboard/incidents`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch incidents');
    return response.json();
  },
  async getAssets() {
    const response = await fetch(`${API_BASE_URL}/dashboard/assets`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch assets total');
    return response.json();
  },
  async getSuppliers() {
    if (USE_MOCK_API) {
      return Promise.resolve(mockDashboardSuppliers);
    }
    const response = await fetch(`${API_BASE_URL}/dashboard/suppliers`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch suppliers');
    return response.json();
  },
  async getCustomers() {
    if (USE_MOCK_API) {
      return Promise.resolve(mockDashboardCustomers);
    }
    const response = await fetch(`${API_BASE_URL}/dashboard/customers`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch customers');
    return response.json();
  },
  async getCompliance() {
    if (USE_MOCK_API) {
      return Promise.resolve(mockCompliance);
    }
    const response = await fetch(`${API_BASE_URL}/dashboard/compliance`, { headers: getAuthHeaders() });
    if (!response.ok) throw new Error('Failed to fetch compliance');
    return response.json();
  }
};

// Spec v7: Framework metrics and Test Owners
export const metricsApi = {
  async getFrameworkMetrics(params?: { from?: string; to?: string }) {
    if (USE_MOCK_API) {
      return Promise.resolve(mockFramework as Record<string, unknown>);
    }
    const qs = new URLSearchParams()
    if (params?.from) qs.append('from', params.from)
    if (params?.to) qs.append('to', params.to)
    const response = await fetch(`${API_BASE_URL}/metrics/framework${qs.toString() ? `?${qs}` : ''}`, {
      headers: getAuthHeaders()
    })
    if (!response.ok) throw new Error('Failed to fetch framework metrics')
    return response.json() as Promise<{ data: Array<{ framework: string; data: Array<{ date: string; value: number }> }> }>
  }
}

export const activityApi = {
  async getTestOwnersActivity(params?: { from?: string; to?: string }) {
    if (USE_MOCK_API) {
      return Promise.resolve(mockTestOwners as { data: Record<string, Array<{ date: string; findings_high: number }>> });
    }
    const qs = new URLSearchParams()
    if (params?.from) qs.append('from', params.from)
    if (params?.to) qs.append('to', params.to)
    const response = await fetch(`${API_BASE_URL}/activity/test-owners${qs.toString() ? `?${qs}` : ''}`, {
      headers: getAuthHeaders()
    })
    if (!response.ok) throw new Error('Failed to fetch test owners activity')
    return response.json() as Promise<{ data: Record<string, Array<{ date: string; findings_high: number }>> }>
  }
}

// Integrations API
export const integrationsApi = {
  // Get all integrations
  getIntegrations: async (): Promise<{
    integrations: Array<{
      id: string;
      moduleName: string;
      moduleType: string;
      description: string | null;
      status: string;
      config: any;
      createdBy: string;
      createdAt: string;
      updatedAt: string;
      creator: {
        id: string;
        username: string;
        fullName: string | null;
      };
      organizationIntegrations: Array<{
        id: string;
        status: string;
        lastSync: string | null;
        organization: {
          id: string;
          name: string;
        };
      }>;
      userIntegrations: Array<{
        id: string;
        status: string;
        lastSync: string | null;
        user: {
          id: string;
          username: string;
          fullName: string | null;
        };
      }>;
    }>;
  }> => {
    const response = await fetch(`${API_BASE_URL}/integrations`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch integrations');
    }
    
    return response.json();
  },

  // Create integration
  createIntegration: async (data: {
    moduleName: string;
    moduleType: string;
    description?: string;
    config?: any;
  }): Promise<{
    message: string;
    integration: any;
  }> => {
    const response = await fetch(`${API_BASE_URL}/integrations`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error('Failed to create integration');
    }
    
    return response.json();
  },

  // Update integration
  updateIntegration: async (id: string, data: {
    moduleName?: string;
    moduleType?: string;
    description?: string;
    config?: any;
    status?: string;
  }): Promise<{
    message: string;
    integration: any;
  }> => {
    const response = await fetch(`${API_BASE_URL}/integrations/${id}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error('Failed to update integration');
    }
    
    return response.json();
  },

  // Delete integration
  deleteIntegration: async (id: string): Promise<{
    message: string;
  }> => {
    const response = await fetch(`${API_BASE_URL}/integrations/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete integration');
    }
    
    return response.json();
  },

  // Connect to organization
  connectToOrganization: async (id: string, organizationId: string, configOverrides?: any): Promise<{
    message: string;
    connection: any;
  }> => {
    const response = await fetch(`${API_BASE_URL}/integrations/${id}/connect-organization`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ organizationId, configOverrides })
    });
    
    if (!response.ok) {
      throw new Error('Failed to connect integration to organization');
    }
    
    return response.json();
  },

  // Connect to user
  connectToUser: async (id: string, userId: string, configOverrides?: any): Promise<{
    message: string;
    connection: any;
  }> => {
    const response = await fetch(`${API_BASE_URL}/integrations/${id}/connect-user`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userId, configOverrides })
    });
    
    if (!response.ok) {
      throw new Error('Failed to connect integration to user');
    }
    
    return response.json();
  },

  // Test integration
  testIntegration: async (id: string): Promise<{
    message: string;
    result: {
      success: boolean;
      message: string;
    };
  }> => {
    const response = await fetch(`${API_BASE_URL}/integrations/${id}/test`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to test integration');
    }
    
    return response.json();
  },

  // Get integration status
  getIntegrationStatus: async (id: string): Promise<{
    status: any;
  }> => {
    const response = await fetch(`${API_BASE_URL}/integrations/${id}/status`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch integration status');
    }
    
    return response.json();
  },

  // Get integration logs
  getIntegrationLogs: async (): Promise<{
    logs: Array<{
      timestamp: string;
      userId: string;
      action: string;
      details: any;
    }>;
  }> => {
    const response = await fetch(`${API_BASE_URL}/integrations/logs`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch integration logs');
    }
    
    return response.json();
  }
};

// Google OAuth API
export const googleAuthApi = {
  // Get Google OAuth URL
  getAuthUrl: async (): Promise<{
    authUrl: string;
  }> => {
    const response = await fetch(`${API_BASE_URL}/auth/google/url`);
    
    if (!response.ok) {
      throw new Error('Failed to get Google auth URL');
    }
    
    return response.json();
  },

  // Handle Google callback
  handleCallback: async (code: string): Promise<{
    message: string;
    token: string;
    user: {
      id: string;
      username: string;
      email: string;
      fullName: string | null;
      role: string;
      avatar: string | null;
      organization: any;
    };
  }> => {
    const response = await fetch(`${API_BASE_URL}/auth/google/callback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ code })
    });
    
    if (!response.ok) {
      throw new Error('Failed to handle Google callback');
    }
    
    return response.json();
  },

  // Verify Google token
  verifyToken: async (idToken: string): Promise<{
    message: string;
    token: string;
    user: {
      id: string;
      username: string;
      email: string;
      fullName: string | null;
      role: string;
      avatar: string | null;
      organization: any;
    };
  }> => {
    const response = await fetch(`${API_BASE_URL}/auth/google/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ idToken })
    });
    
    if (!response.ok) {
      throw new Error('Failed to verify Google token');
    }
    
    return response.json();
  },

  // Disconnect Google account
  disconnect: async (): Promise<{
    message: string;
  }> => {
    const response = await fetch(`${API_BASE_URL}/auth/google/disconnect`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Failed to disconnect Google account');
    }
    
    return response.json();
  }
};

// Two-Factor Authentication API
export const twoFactorApi = {
  setup: async (): Promise<{ qrCode: string; secret: string; manualEntryKey: string }> => {
    const response = await fetch(`${API_BASE_URL}/2fa/setup`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to setup 2FA');
    return response.json();
  },

  verifySetup: async (token: string): Promise<{ backupCodes: string[] }> => {
    const response = await fetch(`${API_BASE_URL}/2fa/verify-setup`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ token })
    });
    if (!response.ok) throw new Error('Failed to verify 2FA setup');
    return response.json();
  },

  verify: async (userId: string, token: string): Promise<{ verified: boolean; backupCodeUsed?: boolean }> => {
    const response = await fetch(`${API_BASE_URL}/2fa/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ userId, token })
    });
    if (!response.ok) throw new Error('Failed to verify 2FA token');
    return response.json();
  },

  disable: async (password: string, token?: string): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/2fa/disable`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ password, token })
    });
    if (!response.ok) throw new Error('Failed to disable 2FA');
  },

  getStatus: async (): Promise<{ enabled: boolean; method: string }> => {
    const response = await fetch(`${API_BASE_URL}/2fa/status`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to get 2FA status');
    return response.json();
  },

  regenerateBackupCodes: async (): Promise<{ backupCodes: string[] }> => {
    const response = await fetch(`${API_BASE_URL}/2fa/regenerate-backup-codes`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to regenerate backup codes');
    return response.json();
  }
};

